import { useImperativeHandle } from 'react';
import type { Ref } from 'react';
import type { ReportContent } from '../../../lib/api/reports.gen';
import { Callout, Skeleton } from '../../../components/ui';
import { useReportPrintHost, type ReportPrintHostHandle } from './useReportPrintHost';
import '../../../styles/reportPagedPreview.css';

// PagedReportPreview — Report Studio's paginated print preview, and since
// story 4-128d an ISOLATED DOCUMENT rather than an in-app render
// (P06 §4.3, P10 §2.3, DOD G-33).
//
// ── What this component was, and what it is now ────────────────────────────
// Until 4-128d this file WAS the frontend's pagination host: it
// `renderToStaticMarkup`'d `<ReportDocument variant="print">` inside the
// application's own React tree, regex-stripped the document's inline
// `<style>@page{…}</style>` (Paged.js fragmented it as flowed content and
// produced a spurious blank leading page), handed the string to a `Previewer`
// it constructed itself, and then reached into the resulting static DOM for
// everything interactive — one delegated native `click` listener on
// `data-report-section-id`, a `classList.toggle` selection ring, an
// `applyTocPageNumbers` DOM walk and a `createPortal` per page for the page
// badges. It worked, and every one of those reaches was correct *given* that
// the paper was rendering inside the app's document.
//
// P06 §4.3 is explicit that it must not: "Isolation from app CSS is
// structural. Host the paginated print document in an isolated iframe/document
// using that artifact in the frontend." The paper now renders inside
// `frontend/public/report-document/index.html` — the content-addressed,
// manifest-hashed artifact story 4-128c publishes — and this file is the
// CHROME around it plus the app end of a typed message channel.
//
// ── Why isolation is worth the channel ─────────────────────────────────────
// Not tidiness. Three concrete things the old shape could not give:
//
//   1. The paper stopped inheriting the application's cascade. Every rule that
//      reaches the document now comes from the artifact's own bundle —
//      `document.base.generated.css` + `document.css` + the packaged fonts —
//      which is the SAME bundle `4-128e`'s worker will load. Two hosts, one
//      stylesheet, is the entire point of DOD G-33; an in-app render can only
//      ever approximate it, because the app's own global CSS is always
//      present.
//   2. The page the worker prints and the page a template author sees are now
//      produced by the same emitted bytes at the same content address, which
//      `identity` below surfaces.
//   3. A future publication on a different origin (an R2 bucket, a CDN — see
//      `report-artifact/README.md` §4) costs a URL change, not a redesign,
//      because nothing here reaches across the boundary by property access.
//
// ── What replaced each DOM reach ───────────────────────────────────────────
//   click delegation   → `printHostBridge` delegates INSIDE the frame and
//                        posts a typed `select` message out. Validated on
//                        four axes before it becomes an `onSelectSection`
//                        call — see `printHostProtocol.ts`.
//   selection ring     → a `select` message; the bridge toggles the same
//                        classes on the same anchors, still without a
//                        re-layout, which is why selection stays instant.
//   TOC page numbers   → unchanged in substance: `pendingTocPageNumbers` is
//                        still set before layout, and `applyTocPageNumbers`
//                        still runs against the real pagination — inside the
//                        artifact, whose `paginate()` returns the resulting
//                        `tocPageByKey` map back over the channel.
//   page badges        → the bridge mounts the same `ReportPageBadge` on the
//                        same `.report-page-badge-host`, on body pages only.
//   `<style>` strip    → `entry.tsx#paginate()` already owned this workaround
//                        for its own use; there is now exactly one copy of it.
//   scroll-to-section  → a `scrollToSection` message. `TemplateEditorPage`'s
//                        outline cannot `scrollIntoView` an element in another
//                        document, so it asks through `hostRef` instead.
//
// ── What stays here, deliberately ──────────────────────────────────────────
// P06 §4.3: "Keep selection handles, toolbar, page shadows/gaps, zoom and
// navigation in the host or in a strictly separate chrome layer; none may
// change the inner paper's layout." The loading Skeleton, the failure Callout,
// the live "N body pages" status line and the frame's own size are chrome and
// live here. The page shadows/gaps could not — an embedding page cannot style
// across the boundary — so they are the "strictly separate chrome layer" the
// same sentence allows: `report-artifact/printHostChrome.css`, scoped under an
// attribute only an embedded frame ever carries.
//
// ── Zoom ───────────────────────────────────────────────────────────────────
// The sentence above is the ONLY place P06 mentions zoom, and it is an
// enumeration of things that must not live in the paper — a placement rule,
// not a feature request. No zoom control exists anywhere in this codebase
// (re-confirmed by grep for this story), and none is invented here. What the
// word denotes concretely in this tree is the preview's responsive scaling:
// `ReportDesignerCanvas` caps this component's column at the template's own
// physical `reportPageWidthMm`, and the frame below fills that column at 100%
// width while the paper inside keeps its physical mm size. That scaling
// authority stays where it already was — in the host's layout — and the
// isolated document adds no second one, exactly as `index.html`'s own shell
// comment requires ("the shell must not introduce a second scaling
// authority"). Browser-native zoom continues to work on the whole page,
// frame included, unaffected by the boundary.
export interface PagedReportPreviewProps {
  content: ReportContent;
  // Story 4-113 (Report Studio foundation) — OPTIONAL, additive. Unchanged
  // prop contract across 4-128d: the canvas still hands down a selection and a
  // callback, and still gets a callback back when a section is clicked. Only
  // the transport under it changed.
  selectedSectionKey?: string | null;
  onSelectSection?: (sectionKey: string) => void;
  /**
   * Story 4-128d — the imperative half of navigation. The paper is in another
   * document, so `TemplateEditorPage`'s outline-click scroll cannot be a
   * `document.querySelector(...).scrollIntoView()` any more; it asks this host
   * to do it. Optional: every other consumer ignores it.
   */
  hostRef?: Ref<ReportPrintHostHandle>;
}

export function PagedReportPreview({
  content,
  selectedSectionKey = null,
  onSelectSection,
  hostRef,
}: PagedReportPreviewProps): JSX.Element {
  const { status, frameUrl, frameRef, identity, pagination, contentHeight, error, handle } = useReportPrintHost({
    content,
    selectedSectionKey,
    onSelectSection,
  });

  useImperativeHandle(hostRef, () => handle, [handle]);

  const bodyPages = pagination?.bodyPageCount ?? 0;
  const frontMatterCount = pagination?.frontMatterPageCount ?? 0;

  return (
    <div
      className="report-paged-preview"
      data-testid="report-paged-preview"
      // The 4-128c freshness contract, made visible where a person or a spec
      // can read it: WHICH artifact this preview is actually running. A stale
      // publication is a wrong preview, and this is the only place in the app
      // that can say so.
      data-report-artifact-source={identity?.sourceDigest.slice(0, 12) ?? undefined}
    >
      {status !== 'ready' && status !== 'error' && (
        <div role="status" className="space-y-3" aria-label="Rendering paginated preview">
          <span className="sr-only">Rendering paginated preview…</span>
          <Skeleton className="mx-auto h-[400px] w-full max-w-2xl" />
        </div>
      )}
      {status === 'error' && (
        <Callout tone="danger" title="Paginated preview failed">
          {error ??
            'The page-by-page preview could not be rendered. The downloaded PDF is unaffected — try the Web preview tab, or reload.'}
        </Callout>
      )}
      {/* Story 4-108 — a real, live-updated count instead of a decorative
          label, now reported by the isolated document's own pagination pass
          rather than counted out of the app's DOM. Read by a screen reader as
          one polite announcement rather than once per page. */}
      {status === 'ready' && (
        <p role="status" aria-live="polite" className="mb-2 text-center text-caption text-muted-foreground">
          {frontMatterCount > 0 ? `${frontMatterCount} front-matter page${frontMatterCount === 1 ? '' : 's'} · ` : ''}
          {bodyPages} body page{bodyPages === 1 ? '' : 's'}
        </p>
      )}
      {frameUrl !== null && (
        <iframe
          ref={frameRef}
          src={frameUrl}
          // P06 §4.3: "The print document has a meaningful title and
          // keyboard/reading access." Two titles matter for an iframe and both
          // are set: THIS one names the frame in the embedding page's
          // accessibility tree (axe's `frame-title` rule), and the bridge sets
          // the inner `document.title` to the real report title once content
          // has rendered, which is what AT announces on entering the frame.
          title="Paginated report preview"
          className="report-paged-preview__frame"
          // Sized to its content, so the EDITOR page scrolls through the page
          // stack exactly as it did before the boundary existed — no nested
          // scroll region, and `scrollIntoView` inside the frame still moves
          // the section to the top of the reader's view.
          style={{ height: contentHeight > 0 ? `${contentHeight}px` : '400px' }}
          // `allow-same-origin` is required (the artifact reads its own
          // `manifest.json` beside itself, which is how it proves WHICH bundle
          // it loaded) and `allow-scripts` is the bundle itself. Everything
          // else stays denied: no forms, no popups, no modals, and critically
          // no `allow-top-navigation` — a report document can never navigate
          // the application away.
          sandbox="allow-scripts allow-same-origin"
          aria-busy={status !== 'ready'}
        />
      )}
    </div>
  );
}

export type { ReportPrintHostHandle } from './useReportPrintHost';
